import CloseRounded from '@mui/icons-material/CloseRounded';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import { createContext, type ReactNode, useCallback, useContext, useState } from 'react';

type Severity = 'success' | 'error' | 'info' | 'warning';

/**
 * What a notification may carry beyond its message. Every field is optional and
 * defaults to the plain toast (bottom-right, 4 s, message only), so a caller that
 * passes nothing gets exactly what it always got.
 */
export interface NotifyOptions {
  /** A bold first line above the message. */
  title?: string;
  /** A button beside the close cross; clicking it runs `onClick` and dismisses. */
  action?: { label: string; onClick: () => void };
  /** Which bottom corner. Default `right`. */
  horizontal?: 'left' | 'right';
  /** Milliseconds before it hides itself. Default 4000. */
  autoHideDuration?: number;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: Severity;
  options: NotifyOptions;
}

interface SnackbarContextValue {
  /** Show a success notification. */
  notifySuccess: (message: string, options?: NotifyOptions) => void;
  /** Show an error notification. */
  notifyError: (message: string) => void;
  /** Show a notification with custom severity. */
  notify: (message: string, severity?: Severity, options?: NotifyOptions) => void;
}

const DEFAULT_AUTO_HIDE_MS = 4000;

const SnackbarContext = createContext<SnackbarContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useSnackbar(): SnackbarContextValue {
  const ctx = useContext(SnackbarContext);
  if (!ctx) throw new Error('useSnackbar must be used within SnackbarProvider');
  return ctx;
}

export default function SnackbarProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
    options: {},
  });

  const notify = useCallback(
    (message: string, severity: Severity = 'info', options: NotifyOptions = {}) => {
      setState({ open: true, message, severity, options });
    },
    [],
  );

  const notifySuccess = useCallback(
    (message: string, options?: NotifyOptions) => notify(message, 'success', options),
    [notify],
  );

  const notifyError = useCallback((message: string) => notify(message, 'error'), [notify]);

  const handleClose = useCallback((_event?: React.SyntheticEvent | Event | null, reason?: string) => {
    if (reason === 'clickaway') return;
    setState((prev) => ({ ...prev, open: false }));
  }, []);

  const { title, action, horizontal = 'right', autoHideDuration } = state.options;

  return (
    <SnackbarContext.Provider value={{ notify, notifySuccess, notifyError }}>
      {children}
      <Snackbar
        open={state.open}
        autoHideDuration={autoHideDuration ?? DEFAULT_AUTO_HIDE_MS}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal }}
      >
        <Alert
          onClose={handleClose}
          severity={state.severity}
          variant="filled"
          // With an action, Alert no longer draws its own cross: both go here.
          action={
            action ? (
              <>
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    action.onClick();
                    handleClose();
                  }}
                  sx={{ fontWeight: 600 }}
                >
                  {action.label}
                </Button>
                <IconButton aria-label="Close" color="inherit" size="small" onClick={handleClose}>
                  <CloseRounded fontSize="small" />
                </IconButton>
              </>
            ) : undefined
          }
          sx={{
            fontWeight: 500,
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            minWidth: 280,
          }}
        >
          {title && <AlertTitle sx={{ fontWeight: 700 }}>{title}</AlertTitle>}
          {state.message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
}
